import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Pagination,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AssignmentOutlined, Add } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Button from '../components/UIKit/Button';

import LearningPathCard from '../components/LearningPathCard';
import LearningPathFilters from '../components/LearningPathFilters';
import { learningPathsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const LearningPaths = () => {
  const { t } = useTranslation(['learningPaths', 'common']);
  const [learningPaths, setLearningPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    difficulty: '',
    professions: [],
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { isAuthenticated, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const itemsPerPage = 8;

  const fetchLearningPaths = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Format filters for API call
      const apiFilters = { ...filters };

      // Convert professions array to comma-separated string if it exists and has items
      if (apiFilters.professions && apiFilters.professions.length > 0) {
        apiFilters.professions = apiFilters.professions.join(',');
      }

      // Добавляем параметры пагинации
      apiFilters.page = page;
      apiFilters.limit = itemsPerPage;

      const response = await learningPathsAPI.getAll(apiFilters);
      
      // Обработка ответа с новой структурой
      let pathsData = [];
      
      if (response && response.data) {
        // Новая структура ответа
        pathsData = Array.isArray(response.data.data) ? response.data.data : [];
        
        const paginationInfo = response.data.pagination || { total: 0, pages: 1 };
        setTotalPages(paginationInfo.pages);
      } else if (Array.isArray(response)) {
        // Старая структура - прямой массив
        pathsData = response;
        setTotalPages(Math.ceil(response.length / itemsPerPage));
      }
      
      setLearningPaths(pathsData);
      setLoading(false);
    } catch (err) {
      console.error(t('learningPaths:error.load'), err);
      setError(t('learningPaths:error.load'));
      setLoading(false);
    }
  }, [filters, page, itemsPerPage, t]);

  useEffect(() => {
    fetchLearningPaths();
  }, [fetchLearningPaths]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Проверка, есть ли черновики учебных дорожек в списке
  const hasDrafts = Array.isArray(learningPaths) && learningPaths.some(path => path.isPublished === false);
  
  // Проверка, является ли пользователь администратором
  const isAdmin = user && user.roles && user.roles.includes('admin');

  return (
    <Container sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        mb={2}
      >
        <Box display="flex" alignItems="center" mb={isMobile ? 2 : 0}>
          <AssignmentOutlined fontSize="large" color="primary" sx={{ mr: 2 }} />
          <Typography variant="h3" component="h1">
            {t('learningPaths:title')}
          </Typography>
        </Box>

        <Box>
          {isAuthenticated && (
            <>
              <Button
                variant="outlined"
                color="primary"
                component={Link}
                to="/learning-paths/my"
                sx={{ mr: 2 }}
              >
                {t('learningPaths:myPaths')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                component={Link}
                to="/learning-paths/create"
              >
                {t('learningPaths:create')}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Box mb={4}>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('learningPaths:description')}
        </Typography>
        
        {isAuthenticated && isAdmin && hasDrafts && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: '10px' }}>
            {t('learningPaths:draftsInfo')}
          </Alert>
        )}
      </Box>

      <LearningPathFilters onFilterChange={handleFilterChange} />

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </>
      ) : !Array.isArray(learningPaths) || learningPaths.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          {t('learningPaths:noPaths')}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {Array.isArray(learningPaths) && learningPaths.map((path) => (
              <Grid item xs={12} md={6} xl={4} xxl={3} key={path._id}>
                <LearningPathCard learningPath={path} />
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default LearningPaths;
